<template>
  <div class="project-description-container">
    <!-- 标题区域 -->
    <div class="title-section">
      <h3 class="title">项目简介</h3>
    </div>

    <!-- 图片轮播区域 -->
    <div class="carousel-section">
      <div class="carousel-container" v-if="displayImages.length > 0">
        <div class="carousel-wrapper" :style="{ transform: `translateX(-${currentImageIndex * 100}%)` }">
          <div
            class="carousel-item"
            v-for="(image, index) in displayImages"
            :key="index"
          >
            <img :src="image" class="carousel-image" @error="handleImageError" />
          </div>
        </div>

        <!-- 轮播控制按钮 -->
        <div v-if="displayImages.length > 1" class="carousel-controls">
          <button
            class="carousel-btn prev-btn"
            @click="prevImage"
            :disabled="currentImageIndex === 0"
          >
            <span>‹</span>
          </button>
          <button
            class="carousel-btn next-btn"
            @click="nextImage"
            :disabled="currentImageIndex === displayImages.length - 1"
          >
            <span>›</span>
          </button>
        </div>

        <!-- 轮播指示器 -->
        <div v-if="displayImages.length > 1" class="carousel-indicators">
          <span
            v-for="(image, index) in displayImages"
            :key="index"
            class="indicator"
            :class="{ active: currentImageIndex === index }"
            @click="currentImageIndex = index"
          ></span>
        </div>
      </div>
    </div>

    <!-- 项目描述区域 -->
    <div class="description-section">
      <div
        ref="descriptionContent"
        class="description-content"
        :class="{ 'truncated': isTextTruncated && !showFullDescription }"
      >
        {{ env.project.description || '暂无项目描述' }}
      </div>

      <!-- 详情按钮 -->
      <div v-if="isTextTruncated" class="details-button-container">
        <button class="details-btn" @click="showDetailDialog = true">
          查看详情
        </button>
      </div>
    </div>

    <!-- 详情对话框 -->
    <div v-if="showDetailDialog" class="dialog-overlay" @click="closeDialog">
      <div class="dialog-content" @click.stop>
        <div class="dialog-header">
          <h4>项目详细描述</h4>
          <button class="close-btn" @click="closeDialog">×</button>
        </div>
        <div class="dialog-body">
          <p class="full-description">{{ env.project.description }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    env: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      currentImageIndex: 0,
      showDetailDialog: false,
      isTextTruncated: false,
      defaultImage: 'https://nbgzfiles-1257839135.cos.ap-shanghai.myqcloud.com/assets/rwxavatar.png',
    };
  },
  computed: {
    displayImages() {
      const images = this.env?.project?.images || [];
      return images.length > 0 ? images : [this.defaultImage];
    },
  },
  mounted() {
    this.checkTextTruncation();
    // 监听窗口大小变化，重新检查文本截断
    window.addEventListener('resize', this.checkTextTruncation);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkTextTruncation);
  },
  watch: {
    'env.project.description': {
      handler() {
        this.$nextTick(() => {
          this.checkTextTruncation();
        });
      },
    },
  },
  methods: {
    nextImage() {
      if (this.currentImageIndex < this.displayImages.length - 1) {
        this.currentImageIndex++;
      }
    },
    prevImage() {
      if (this.currentImageIndex > 0) {
        this.currentImageIndex--;
      }
    },
    handleImageError(event) {
      // 图片加载失败时使用默认图片
      event.target.src = this.defaultImage;
    },
    checkTextTruncation() {
      this.$nextTick(() => {
        const element = this.$refs.descriptionContent;
        if (element) {
          this.isTextTruncated = element.scrollHeight > element.clientHeight;
        }
      });
    },
    closeDialog() {
      this.showDetailDialog = false;
    },
  },
};
</script>

<style scoped>
.project-description-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
  box-sizing: border-box;
}

.title-section {
  margin-bottom: 16px;
}

.title {
  font-size: 18px;
  font-weight: bold;
  margin: 0;
  color: var(--text-primary, #333);
}

.carousel-section {
  margin-bottom: 16px;
}

.carousel-container {
  position: relative;
  width: 100%;
  height: 200px;
  border-radius: 10px;
  overflow: hidden;
  background: var(--border, #f0f0f0);
}

.carousel-wrapper {
  display: flex;
  width: 100%;
  height: 100%;
  transition: transform 0.3s ease;
}

.carousel-item {
  min-width: 100%;
  height: 100%;
}

.carousel-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 10px;
}

.carousel-controls {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 0 12px;
  pointer-events: none;
}

.carousel-btn {
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  color: white;
  font-size: 18px;
  cursor: pointer;
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.carousel-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.7);
}

.carousel-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.carousel-indicators {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
}

.indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: background-color 0.2s;
}

.indicator.active {
  background: white;
}

.description-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.description-content {
  flex: 1;
  color: var(--primary, #666);
  font-size: 14px;
  line-height: 1.5;
  word-wrap: break-word;
  overflow: hidden;
}

.description-content.truncated {
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.details-button-container {
  margin-top: 12px;
  text-align: center;
}

.details-btn {
  background: var(--primary, #007bff);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.details-btn:hover {
  background: var(--primary-dark, #0056b3);
}

.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.dialog-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--border, #eee);
}

.dialog-header h4 {
  margin: 0;
  font-size: 18px;
  color: var(--text-primary, #333);
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--text-secondary, #666);
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.close-btn:hover {
  background: var(--border, #f0f0f0);
}

.dialog-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.full-description {
  color: var(--text-primary, #333);
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
}
</style>
